import { Component, createRef } from 'react';

import { IOC_TYPES } from '../../../context/ioc.types';
import { lazyInject } from '../../../context/module';
import { FileUpload } from '../../../models/file-upload';
import { FormControl } from '../../../models/forms';
import { AuthService } from '../../../services/auth.service';
import { FileDownloadService } from '../../../services/file-download.service';
import { UIContext } from '../../../services/ui-context';
import { ProcessTypes, ProcessorMetadata } from '../../../types/types';
import { BrowserUtils } from '../../../ui-utils/browser-utils';
import { CCanvas } from '../../../utils/ccanvas';
import { ConvertOptionsUtils } from '../../../utils/convert-options-utils';
import { FilenameUtils } from '../../../utils/filename-utils';
import { findIndex } from '../../../utils/lodash-min';
import Modal from '../modal/modal.component';
import { ProcessorMetadataUtils } from '../../../utils/processor-metadata.utils';


declare var pdfjsLib;

interface SingleFileDownloadModalComponentProps {
    title: string;
    files: any[];
    name: string;
    numberOfFiles: number;
    fileInfo: FileUpload;
    closeBtnName: string;
}

export class SingleFileDownloadModalComponent extends Component<SingleFileDownloadModalComponentProps> {
    list: any[] = [];

    hasThumnails = false;
    pdfThumDataURLMap = new Map<string, string>();
    readyPromise: Promise<any>;

    showPreview = new FormControl(false);
    processed = {};

    modalRef = createRef<Modal>();
    private generalFileIndex = 0;
    // @ViewChildren('thumbCanvases') thumbCanvases: QueryList<ElementRef>;

    @lazyInject(IOC_TYPES.FileDownloadService) private downloadService: FileDownloadService;
    @lazyInject(IOC_TYPES.AuthService) public auth: AuthService;
    @lazyInject(IOC_TYPES.UIContext) private uiContext: UIContext;

    constructor(
        props
    ) {
        super(props);
        this.readyPromise = Promise.all<{}>([
            new Promise<{}>((resolve, reject) => {
                if (!BrowserUtils.hasWindow() || window['pdfjsLib']) {
                    resolve({});
                } else {
                    const node = document.createElement('script');
                    node.src = '/js/pdf.min.js';
                    node.type = 'text/javascript';
                    node.async = true;
                    node.charset = 'utf-8';
                    (node as any).onload = resolve;
                    node.onerror = reject;
                    document.getElementsByTagName('head')[0].appendChild(node);
                }
            }),
            new Promise<{}>((resolve, reject) => {
                // TODO Fix this for prerender
                if (!BrowserUtils.hasWindow() || window['pdfjsWorker']) {
                    resolve({});
                } else {
                    const node = document.createElement('script');
                    node.src = '/js/pdf.worker.min.js';
                    node.type = 'text/javascript';
                    node.async = true;
                    node.charset = 'utf-8';
                    (node as any).onload = resolve;
                    node.onerror = reject;
                    document.getElementsByTagName('head')[0].appendChild(node);
                }
            }),
        ]);

        this.showPreview.valueChanges.subscribe(
            val => {
                this.update().catch((err) => console.log(err));
            },
        );
    }

    componentDidMount() {
        if (this.props.fileInfo.processType === 'split' && this.props.fileInfo.toType === 'pdf') {
            this.hasThumnails = true;
        }
    }

    async update() {
        this.readyPromise.then(async () => {
            if (this.showPreview.value) {
                for (const el of Array.from(document.querySelectorAll<HTMLCanvasElement>('.thumbCanvases'))) {
                    const url = el.getAttribute('data-url');
                    const file_name = el.getAttribute('data-filename');
                    await this.pdfjsThumb(el, file_name, url);
                }
            }
        }).catch((err) => console.log(err));
    }

    onDownloadBtnHandler(file_name) {
        this.downloadService.downloadFile(this.props.fileInfo, file_name);
    }

    directDownloadHandler(file_name) {
        const link = this.downloadService.getDirectDownloadLink(this.props.fileInfo, file_name);
        this.uiContext.showSuccessDialog("Download Links", null, `
                    <div style="text-align: left;">
                        <div>
                            Filename: 
                            <div className="alert alert-info word-break-all">${file_name}</div>
                        </div>
                        <div>
                            Link:
                            <div className="alert alert-info word-break-all">${link}</div>
                        </div>
                        <div>*To download file directly, copy and paste above link to your browser's address bar.</div>
                    </div>
                `);
    }

    getDownloadURL(file_name) {
        return this.downloadService.getDownloadURL(this.props.fileInfo, file_name);
    }

    getThumbnailUrl(file_name) {
        const processorMetadata = this.props.fileInfo.metadata.processorMetadataList[0];

        const gcName = this.getGCNameFromOutputFilename(file_name);

        // TODO what happen if token is null
        let url = ProcessorMetadataUtils.getThumbnailEndpoint(processorMetadata)
            + `/thumbnail?token=null&gcName=${gcName}` +
            `&uploadType=${processorMetadata.uploadType}&safeName=${processorMetadata.fileMetadata.safeName}`;

        const authToken = this.auth.getToken();
        if (authToken) {
            url = `${url}&authorization=${authToken}`;
        }

        return url;
    }

    getGCNameFromOutputFilename(file_name: string) {
        const index = file_name.indexOf('?');
        if (index !== -1) {
            return file_name.substring(1, index);
        }
        return file_name;
    }

    decodeFileName(name: string) {
        this.props.name;
        const fileNameTokens = (this.getAcualFileOutputFromOutput(name) || name).split(/-z-|-y-/);
        const fname = fileNameTokens[fileNameTokens.length - 1];

        if (this.props.fileInfo.fromType.includes('archive')) {
            return fname;
        }

        let index = fileNameTokens[fileNameTokens.length - 2];

        if (this.props.fileInfo.processType === ProcessTypes.SPLIT) {
            const splitType = ConvertOptionsUtils.getPropertyValue(this.props.fileInfo, 'split');
            if (splitType === 'page_by_page') {
                index = this.getOutputIndex(name) + 1;
            }
            return 'Page ' + index + ' of ' + fname;
        }

        return this.getIndexedFileName(name, index);
    }

    private getIndexedFileName(outputFilename: string, index: number) {
        const inputFileBasename = FilenameUtils.getBasename(this.props.name);
        const outputFileExtension = FilenameUtils.getExtension(outputFilename);
        return `${inputFileBasename} ${index}.${outputFileExtension}`;
    }

    private getAcualFileOutputFromOutput(output_fname) {
        try {
            const index = this.getOutputIndex(output_fname);
            return (this.props.fileInfo.result.originalOutput || this.props.fileInfo.result.output)[index];
        } catch (e) {
            console.error(e);
        }
    }

    private getOutputIndex(output_fname) {
        try {
            return findIndex(this.props.fileInfo.result.output, f => f === output_fname);
        } catch (e) {
            console.error(e);
        }
        return -1;
    }

    async pdfjsThumb(canvas: HTMLCanvasElement, file_name: string, pdfUrl: string) {
        // this.ngZone.runOutsideAngular(async () => {
        const pdfDocument = await pdfjsLib.getDocument(pdfUrl);
        //
        // Fetch the first page
        //
        const page = await pdfDocument.getPage(1);

        const viewport = page.getViewport(1);
        //
        // Prepare canvas using PDF page dimensions
        //
        const context = canvas.getContext('2d');
        canvas.height = viewport.height;
        canvas.width = viewport.width;
        //
        // Render PDF page into canvas context
        //
        const task = page.render({ canvasContext: context, viewport: viewport });
        await task.promise.then(() => {
            // this.ngZone.run(() => {
            const displayCanvas = canvas.closest('div').querySelector<HTMLCanvasElement>('.display-canvas');
            CCanvas.copyCanvas(canvas, displayCanvas);
            // this.pdfThumDataURLMap.set(file_name, canvas.toDataURL('image/jpeg'));
            // });
        });
        // });
    }

    getPDFThumDataURL(file_name: string) {
        return this.pdfThumDataURLMap.get(file_name);
    }

    getModalBody() {
        if (this.hasThumnails) {
            return <div data-modalbody>
                <div className="row padding-bottom-10 justify ml-2 mr-2">
                    {
                        this.props.files.map(file_name => {
                            return <div className="card ml-1 mr-1 mb-1">
                                <div style={{ width: '9rem' }}>
                                    <canvas className="card-img-top display-canvas"></canvas>
                                    {
                                        false && this.getPDFThumDataURL(file_name) && <img src={this.getPDFThumDataURL(file_name)}
                                            className="card-img-top" alt="..." />
                                    }
                                    <canvas className="thumbCanvases" width="9rem" style={{ display: 'none' }} data-filename="file_name"
                                        data-url={this.getDownloadURL(file_name)}></canvas>
                                    <div className="card-body">
                                        <span className="card-title">{this.decodeFileName(file_name)}</span>
                                        <button className="x-btn x-btn-primary x-btn-sm download-btn m-1" data-url={this.getDownloadURL(file_name)}
                                            onClick={() => this.onDownloadBtnHandler(file_name)}>Download</button>
                                        <button className="x-btn x-btn-gray x-btn-sm download-link-view-btn m-1"
                                            data-url={this.getDownloadURL(file_name)} onClick={() => this.directDownloadHandler(file_name)}>
                                            Link
                                        </button>
                                    </div>
                                </div>
                            </div>
                        })
                    }
                </div>
            </div>;
        } else {
            return <div data-modalbody>
                {
                    this.props.files.map(file_name => {
                        return <div className="row padding-bottom-10">
                            <div className="col-sm-7 col-md-8 col-lg-8">
                                <div className="file-name ellipsis">
                                    {this.decodeFileName(file_name)}
                                </div>
                            </div>
                            <div className="col-sm-5 col-md-4 col-lg-4 text-right">
                                <button className="x-btn x-btn-primary x-btn-sm download-btn m-1" data-url={this.getDownloadURL(file_name)}
                                    onClick={() => this.onDownloadBtnHandler(file_name)}>Download</button>
                                <button className="x-btn x-btn-gray x-btn-sm download-link-view-btn m-1"
                                    data-url={this.getDownloadURL(file_name)} onClick={() => this.directDownloadHandler(file_name)}>
                                    Link
                                </button>
                            </div>
                        </div>

                    })
                }
            </div>
        }
    }

    render() {
        console.log(this.props);
        return <Modal ref={this.modalRef} >
            <div data-modalheader>
                {this.props.title}
                <div id="quickActionMotive" className="quick-action-item motive custom-control custom-switch"
                    aria-describedby="quickActionMotive">
                    <input onChange={(e) => this.showPreview.setValue(e)}
                        type="checkbox" className="custom-control-input" id="showPreviewSwitch" />
                    <label className="custom-control-label" htmlFor="showPreviewSwitch">Show Preview</label>
                </div>
            </div>
            {
                this.getModalBody()
            }
            <div data-modalfooter>
                <button type="button" className="x-btn x-btn-gray">{this.props.closeBtnName || 'Close'}</button>
            </div>
        </Modal>
    }
}
